import React, { useEffect, useMemo, useRef, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerClientes, crearCliente, actualizarCliente, eliminarCliente, obtenerComprasDeCliente, obtenerVentasDeClientes } from '../services/clientesService';
import { obtenerCotizaciones } from '../services/cotizacionesService';
import { obtenerServicios } from '../services/serviciosService';
import { mensajeAmigable } from '../utils/errores';
import { limpiarTelefono, telefonoValido, emailValido, limpiarTexto } from '../utils/validacion';
import { linkWhatsAppA, numeroWhatsApp, copiarTexto } from '../utils/compartir';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import UbicacionCliente from './UbicacionCliente';
import { esViajes as esViajesPerfil } from '../utils/perfil';
import CampanaRecompraView from './CampanaRecompraView';

const VACIO = { nombre: '', telefono: '', email: '', notas: '', esLocal: true, ciudad: '', destinos: [], servicios: [], fechaUltimaCompra: '' };

const inputBase = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

// Segmentos del CRM: para dar seguimiento según lo que hizo cada cliente.
const SEGMENTOS = [
  { v: '', l: 'Todos los clientes' },
  { v: 'con_compra', l: '🛍️ Compraron' },
  { v: 'sin_compra', l: '💤 Sin compras' },
  { v: 'cotizo_vio', l: '👀 Cotizaron y solo vieron' },
  { v: 'aprobada', l: '✅ Aprobaron la cotización' },
  { v: 'convertida', l: '💰 Cotización convertida en venta' },
];

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Clave para agrupar textos escritos distinto ("Tegucigalpa" = "TEGUCIGALPA",
// "Cancún" = "cancun"): sin tildes, minúsculas y espacios normalizados.
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

// Opciones únicas para un filtro: { k: clave normalizada, l: cómo se muestra }.
const opciones = (arr) => {
  const m = new Map();
  arr.forEach((x) => { const k = norm(x); if (k && !m.has(k)) m.set(k, String(x).trim()); });
  return [...m.entries()].map(([k, l]) => ({ k, l })).sort((a, b) => a.l.localeCompare(b.l));
};

// Reemplaza {nombre} por el primer nombre del cliente.
const personalizar = (txt, c) => (txt || '').replace(/\{nombre\}/gi, (c.nombre || '').trim().split(' ')[0] || '');

// Menú desplegable de selección múltiple DENTRO del campo: al tocar el campo se
// abre la lista (con scroll, sirve aunque haya 50+ opciones), escribir la
// filtra, cada opción se marca/desmarca con su casilla, y si no existe se
// agrega desde el mismo menú ("➕ Agregar …"). Los elegidos quedan como
// etiquetas dentro del campo. Sin duplicados por mayúsculas/tildes.
function ChipsInput({ valores, onChange, sugerencias, placeholder, disabled, color = '#7C3AED', fondo = '#F5F3FF' }) {
  const [entrada, setEntrada] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0); // opción resaltada (teclado)
  const cajaRef = useRef(null);
  const inputRef = useRef(null);

  const elegidos = new Set(valores.map(norm));
  const buscar = norm(entrada);
  const texto = limpiarTexto(entrada, 80).trim();
  const coincidencias = sugerencias.filter((s) => !buscar || norm(s).includes(buscar));
  const nuevo = buscar && !sugerencias.some((s) => norm(s) === buscar) && !elegidos.has(buscar);
  const items = [...(nuevo ? [{ tipo: 'nuevo', v: texto }] : []), ...coincidencias.map((s) => ({ tipo: 'op', v: s }))];

  // Cerrar al tocar fuera del campo.
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (cajaRef.current && !cajaRef.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('touchstart', fuera);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('touchstart', fuera);
    };
  }, [abierto]);

  const alternar = (v) => {
    if (elegidos.has(norm(v))) onChange(valores.filter((x) => norm(x) !== norm(v)));
    else onChange([...valores, v]);
  };
  const elegir = (it) => {
    if (!it) return;
    alternar(it.v);
    if (it.tipo === 'nuevo') setEntrada('');
    setActivo(0);
    if (inputRef.current) inputRef.current.focus();
  };
  const quitar = (v) => onChange(valores.filter((x) => x !== v));

  const teclas = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAbierto(true); setActivo((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (abierto) elegir(items[activo]); else setAbierto(true); }
    else if (e.key === 'Escape') setAbierto(false);
    else if (e.key === 'Backspace' && !entrada && valores.length) quitar(valores[valores.length - 1]);
  };

  return (
    <div ref={cajaRef} style={{ position: 'relative' }}>
      <div onClick={() => { if (!disabled) { setAbierto(true); if (inputRef.current) inputRef.current.focus(); } }}
        style={{ ...inputBase, display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', minHeight: '44px', padding: '6px 34px 6px 8px', cursor: 'text', background: 'white', border: `1px solid ${abierto ? color : '#D1D5DB'}`, position: 'relative' }}>
        {valores.map((v) => (
          <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: color, color: 'white', borderRadius: '999px', padding: '4px 10px', fontSize: '13px', fontWeight: 600 }}>
            {v}
            <button type="button" disabled={disabled} aria-label={`Quitar ${v}`}
              onClick={(e) => { e.stopPropagation(); quitar(v); }}
              style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '15px', lineHeight: 1, padding: 0 }}>×</button>
          </span>
        ))}
        <input ref={inputRef} value={entrada} disabled={disabled} maxLength={80}
          onChange={(e) => { setEntrada(e.target.value); setAbierto(true); setActivo(0); }}
          onFocus={() => setAbierto(true)}
          onKeyDown={teclas}
          placeholder={valores.length ? 'Agregar otro…' : placeholder}
          style={{ flex: '1 1 160px', minWidth: '120px', border: 'none', outline: 'none', fontSize: '14px', padding: '4px', background: 'transparent' }} />
        <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', pointerEvents: 'none', fontSize: '11px' }}>{abierto ? '▲' : '▼'}</span>
      </div>

      {abierto && !disabled && (
        <div role="listbox" aria-multiselectable="true"
          style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', boxShadow: '0 10px 25px rgba(0,0,0,0.12)', maxHeight: '260px', overflowY: 'auto', zIndex: 50 }}>
          {items.length === 0 && (
            <p style={{ margin: 0, padding: '12px', fontSize: '13px', color: '#9CA3AF' }}>
              {sugerencias.length ? 'Sin coincidencias.' : 'Tu lista está vacía. Escribe para agregar el primero.'}
            </p>
          )}
          {items.map((it, i) => {
            const marcado = it.tipo === 'op' && elegidos.has(norm(it.v));
            return (
              <div key={`${it.tipo}-${it.v}`} role="option" aria-selected={marcado}
                onMouseDown={(e) => { e.preventDefault(); elegir(it); }}
                onMouseEnter={() => setActivo(i)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', cursor: 'pointer', fontSize: '14px', background: i === activo ? fondo : 'white', color: it.tipo === 'nuevo' ? color : '#111827', fontWeight: it.tipo === 'nuevo' ? 700 : 400, borderBottom: '1px solid #F9FAFB' }}>
                {it.tipo === 'nuevo' ? (
                  <span>➕ Agregar "{it.v}"</span>
                ) : (
                  <>
                    <span style={{ width: '18px', height: '18px', borderRadius: '5px', flexShrink: 0, border: `2px solid ${marcado ? color : '#D1D5DB'}`, background: marcado ? color : 'white', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>{marcado ? '✓' : ''}</span>
                    <span>{it.v}</span>
                  </>
                )}
              </div>
            );
          })}
          {valores.length > 0 && (
            <div style={{ position: 'sticky', bottom: 0, background: 'white', borderTop: '1px solid #F3F4F6', padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#6B7280' }}>{valores.length} elegido(s)</span>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); setAbierto(false); setEntrada(''); }}
                style={{ padding: '6px 14px', background: color, color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>
                Listo
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Fondo oscuro + tarjeta para los modales de envío.
function Modal({ titulo, onCerrar, children }) {
  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '16px', padding: '22px', width: '520px', maxWidth: '100%', maxHeight: '88vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, color: 'var(--color-primario,#DB2777)', fontSize: '18px' }}>{titulo}</h3>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function ClientesView({ onVolver }) {
  const { mostrarToast } = useToast();
  const { puede } = useAuth();
  const esVendedora = !puede('borrar'); // sin permiso "Borrar registros" (también lo bloquea la BD)
  const { negocio } = useAuth();
  // Los campos de destino/servicio/fecha son para agencias de viajes: se
  // muestran solo si el rubro del negocio es 'viajes' o activó el modo viajes.
  const esViajes = esViajesPerfil(negocio);
  const [clientes, setClientes] = useState(null);
  const [verCampana, setVerCampana] = useState(false); // 🔁 Campaña de recompra (sql/124)
  const [ventas, setVentas] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [catalogoServicios, setCatalogoServicios] = useState([]); // nombres del módulo Servicios
  const [error, setError] = useState(null);
  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [filtroSegmento, setFiltroSegmento] = useState('');
  const [filtroCiudad, setFiltroCiudad] = useState('');
  const [filtroProducto, setFiltroProducto] = useState('');
  const [filtroDestino, setFiltroDestino] = useState('');
  const [filtroServicio, setFiltroServicio] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  // Formulario
  const [form, setForm] = useState(VACIO);
  const [editandoId, setEditandoId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [detalle, setDetalle] = useState(null); // { cliente, compras }
  // Selección y envíos
  const [sel, setSel] = useState(() => new Set());
  const [modal, setModal] = useState(null); // 'whatsapp' | 'correo' | null
  const [mensaje, setMensaje] = useState('¡Hola {nombre}! 👋 ');
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [abiertos, setAbiertos] = useState(() => new Set());

  const cargar = async () => {
    setError(null);
    try {
      // Ventas y cotizaciones son "extra" del CRM: si fallan, igual se muestran los clientes.
      const [cs, vs, qs, sv] = await Promise.all([
        obtenerClientes(),
        obtenerVentasDeClientes().catch(() => []),
        obtenerCotizaciones().catch(() => []),
        esViajes ? obtenerServicios().catch(() => []) : Promise.resolve([]),
      ]);
      setClientes(cs);
      setVentas(vs);
      setCotizaciones(qs);
      setCatalogoServicios(sv.filter((s) => s.activo !== false).map((s) => s.nombre).filter(Boolean));
    } catch (e) {
      setError(mensajeAmigable(e));
    }
  };

  // Carga una sola vez al abrir (esViajes ya viene de la sesión).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const guardar = async (e) => {
    e.preventDefault();
    if (form.nombre.trim().length < 2) {
      mostrarToast('Escribe el nombre del cliente', 'error');
      return;
    }
    if (form.telefono && !telefonoValido(form.telefono)) {
      mostrarToast('El teléfono debe tener al menos 8 números (o déjalo vacío)', 'error');
      return;
    }
    if (!emailValido(form.email)) {
      mostrarToast('Ese correo no parece válido (ej. nombre@correo.com)', 'error');
      return;
    }
    setGuardando(true);
    try {
      if (editandoId) {
        await actualizarCliente(editandoId, form);
        mostrarToast('Cliente actualizado ✅', 'exito');
      } else {
        await crearCliente(form);
        mostrarToast('Cliente agregado ✅', 'exito');
      }
      setForm(VACIO);
      setEditandoId(null);
      cargar();
    } catch (err) {
      mostrarToast(mensajeAmigable(err), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const editar = (c) => {
    setEditandoId(c.id);
    setForm({
      nombre: c.nombre, telefono: c.telefono || '', email: c.email || '', notas: c.notas || '',
      esLocal: c.es_local !== false, ciudad: c.ciudad || '',
      destinos: c.destinos || [], servicios: c.servicios || [], fechaUltimaCompra: c.fecha_ultima_compra || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const borrar = async (c) => {
    if (!window.confirm(`¿Eliminar a ${c.nombre}? Sus ventas quedarán sin cliente asociado.`)) return;
    try {
      await eliminarCliente(c.id);
      mostrarToast('Cliente eliminado 🗑️', 'exito');
      setSel((prev) => { const n = new Set(prev); n.delete(c.id); return n; });
      cargar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  const verHistorial = async (c) => {
    try {
      const compras = await obtenerComprasDeCliente(c.id);
      setDetalle({ cliente: c, compras });
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    }
  };

  const fmt = (n) => `${simboloMoneda()} ${Number(n).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtFecha = (iso) => {
    if (!iso) return '';
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString('es-HN', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const input = inputBase;

  // ---- Datos agregados por cliente (compras y cotizaciones) ----
  const compraPorCliente = useMemo(() => {
    const m = {};
    ventas.forEach((v) => {
      if (!v.cliente_id) return;
      const a = m[v.cliente_id] || (m[v.cliente_id] = { productos: new Set(), num: 0, total: 0, ultima: '' });
      if (v.nombre_producto) a.productos.add(norm(v.nombre_producto));
      a.num += 1;
      a.total += Number(v.total || 0);
      const f = String(v.vendido_en || '').slice(0, 10);
      if (f > a.ultima) a.ultima = f;
    });
    return m;
  }, [ventas]);

  const estadosPorCliente = useMemo(() => {
    const m = {};
    cotizaciones.forEach((q) => {
      if (!q.cliente_id) return;
      (m[q.cliente_id] || (m[q.cliente_id] = new Set())).add(q.estado);
    });
    return m;
  }, [cotizaciones]);

  // Última compra efectiva: la fecha registrada a mano (viajes) o la de la última venta.
  const ultimaCompra = (c) => c.fecha_ultima_compra || compraPorCliente[c.id]?.ultima || '';

  const cumpleSegmento = (c, seg) => {
    if (!seg) return true;
    const est = estadosPorCliente[c.id] || new Set();
    const compro = !!compraPorCliente[c.id];
    if (seg === 'con_compra') return compro;
    if (seg === 'sin_compra') return !compro;
    if (seg === 'cotizo_vio') return (est.has('enviada') || est.has('vista')) && !est.has('aprobada') && !est.has('convertida');
    if (seg === 'aprobada') return est.has('aprobada') && !est.has('convertida');
    if (seg === 'convertida') return est.has('convertida');
    return true;
  };

  // Listas para los filtros (a partir de lo que ya existe).
  const ciudades = opciones((clientes || []).map((c) => (c.es_local === false ? c.ciudad : null)));
  const productos = opciones(ventas.map((v) => v.nombre_producto));
  const destinos = opciones((clientes || []).flatMap((c) => c.destinos || []));
  const servicios = opciones([...(clientes || []).flatMap((c) => c.servicios || []), ...catalogoServicios]);

  const texto = busqueda.trim().toLowerCase();
  const hayFiltros = filtroSegmento || filtroCiudad || filtroProducto || filtroDestino || filtroServicio || fechaDesde || fechaHasta;
  const filtrados = (clientes || []).filter((c) => {
    const dArr = c.destinos || [];
    const sArr = c.servicios || [];
    const coincideTexto = !texto
      || (c.nombre || '').toLowerCase().includes(texto)
      || (c.telefono || '').includes(texto)
      || (c.email || '').toLowerCase().includes(texto)
      || (esViajes && (dArr.join(' ').toLowerCase().includes(texto) || sArr.join(' ').toLowerCase().includes(texto)));
    const f = ultimaCompra(c);
    return coincideTexto
      && cumpleSegmento(c, filtroSegmento)
      && (!filtroCiudad || (c.es_local === false && norm(c.ciudad) === filtroCiudad))
      && (!filtroProducto || compraPorCliente[c.id]?.productos.has(filtroProducto))
      && (!filtroDestino || dArr.some((d) => norm(d) === filtroDestino))
      && (!filtroServicio || sArr.some((s) => norm(s) === filtroServicio))
      && (!fechaDesde || (f && f >= fechaDesde))
      && (!fechaHasta || (f && f <= fechaHasta));
  });

  const limpiarFiltros = () => {
    setFiltroSegmento(''); setFiltroCiudad(''); setFiltroProducto('');
    setFiltroDestino(''); setFiltroServicio(''); setFechaDesde(''); setFechaHasta('');
  };

  // ---- Estadísticas ----
  const stats = useMemo(() => {
    const cs = clientes || [];
    const hoy = new Date();
    const clave = (d) => `${d.getFullYear()}-${d.getMonth()}`;
    const porMes = {};
    cs.forEach((c) => {
      if (!c.creado_en) return;
      const k = clave(new Date(c.creado_en));
      porMes[k] = (porMes[k] || 0) + 1;
    });
    const serie = [];
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
      serie.push({ etiqueta: MESES[d.getMonth()], n: porMes[clave(d)] || 0 });
    }
    const conCompra = cs.filter((c) => compraPorCliente[c.id]).length;
    return { total: cs.length, nuevosMes: porMes[clave(hoy)] || 0, conCompra, serie };
  }, [clientes, compraPorCliente]);
  const maxSerie = Math.max(1, ...stats.serie.map((s) => s.n));

  // ---- Selección ----
  const toggleSel = (id) => setSel((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const todosFiltradosSel = filtrados.length > 0 && filtrados.every((c) => sel.has(c.id));
  const toggleTodos = () => {
    if (todosFiltradosSel) {
      setSel((prev) => { const n = new Set(prev); filtrados.forEach((c) => n.delete(c.id)); return n; });
    } else {
      setSel((prev) => { const n = new Set(prev); filtrados.forEach((c) => n.add(c.id)); return n; });
    }
  };
  const seleccionados = (clientes || []).filter((c) => sel.has(c.id));
  const conTelefono = seleccionados.filter((c) => c.telefono);
  const conEmail = seleccionados.filter((c) => c.email);

  const abrirModal = (tipo) => { setAbiertos(new Set()); setModal(tipo); };

  const abrirWhatsApp = (c) => {
    window.open(linkWhatsAppA(c.telefono, personalizar(mensaje, c)), '_blank', 'noopener,noreferrer');
    setAbiertos((prev) => new Set(prev).add(c.id));
  };

  const copiarNumeros = async () => {
    const nums = conTelefono.map((c) => `+${numeroWhatsApp(c.telefono)}`).join('\n');
    const ok = await copiarTexto(nums);
    mostrarToast(ok ? `📋 ${conTelefono.length} número(s) copiados` : 'No se pudo copiar', ok ? 'exito' : 'error');
  };

  const copiarMensaje = async () => {
    const ok = await copiarTexto(mensaje.replace(/\{nombre\}/gi, '').replace(/\s+!/, '!'));
    mostrarToast(ok ? '📋 Mensaje copiado' : 'No se pudo copiar', ok ? 'exito' : 'error');
  };

  const abrirCorreoLote = () => {
    const bcc = conEmail.map((c) => encodeURIComponent(c.email)).join(',');
    window.location.href = `mailto:?bcc=${bcc}&subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`;
  };

  const etiquetaFiltro = { fontSize: '11px', color: '#6B7280', fontWeight: 600, display: 'block', marginBottom: '2px' };
  const selectF = { ...input, backgroundColor: 'white', padding: '8px' };
  const tarjetaStat = { background: 'white', borderRadius: '12px', padding: '12px 14px', border: '1px solid #F3F4F6', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };

  if (verCampana) return <CampanaRecompraView onVolver={() => setVerCampana(false)} />;

  return (
    <div className="k-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="k-page-title">👥 Clientes</h2>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => setVerCampana(true)} title="Escríbeles a las que ya compraron para que vuelvan"
            style={{ padding: '8px 14px', borderRadius: '10px', border: 'none', background: 'var(--color-primario,#DB2777)', color: 'white', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            🔁 Campaña de recompra
          </button>
          <button onClick={onVolver} className="k-btn-soft">
            ← Volver
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      {clientes && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '16px' }}>
          <div style={tarjetaStat}>
            <p style={{ margin: 0, fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Total de clientes</p>
            <p style={{ margin: '2px 0 0', fontSize: '26px', fontWeight: 800, color: '#111827' }}>{stats.total}</p>
          </div>
          <div style={tarjetaStat}>
            <p style={{ margin: 0, fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Nuevos este mes</p>
            <p style={{ margin: '2px 0 0', fontSize: '26px', fontWeight: 800, color: '#059669' }}>+{stats.nuevosMes}</p>
          </div>
          <div style={tarjetaStat}>
            <p style={{ margin: 0, fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Han comprado</p>
            <p style={{ margin: '2px 0 0', fontSize: '26px', fontWeight: 800, color: 'var(--color-primario,#DB2777)' }}>{stats.conCompra}</p>
          </div>
          <div style={{ ...tarjetaStat, gridColumn: 'span 1' }}>
            <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Nuevos por mes</p>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '52px' }}>
              {stats.serie.map((s) => (
                <div key={s.etiqueta} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                  <span style={{ fontSize: '10px', color: '#374151', fontWeight: 700 }}>{s.n}</span>
                  <div style={{ width: '100%', height: `${Math.max(3, (s.n / maxSerie) * 34)}px`, background: 'var(--color-primario,#DB2777)', borderRadius: '4px 4px 0 0', opacity: s.n ? 0.85 : 0.2 }} />
                  <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{s.etiqueta}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={guardar} style={{ backgroundColor: '#FFF5F7', borderRadius: '12px', padding: '16px', border: '1px solid #FBCFE8', marginBottom: '20px' }}>
        <p style={{ margin: '0 0 12px', fontWeight: 'bold', color: 'var(--color-primario,#DB2777)' }}>
          {editandoId ? '✏️ Editar cliente' : '➕ Nuevo cliente'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '10px' }}>
          <input value={form.nombre} disabled={guardando} maxLength={80} onChange={(e) => setForm({ ...form, nombre: limpiarTexto(e.target.value, 80) })} style={input} placeholder="Nombre *" />
          <input type="tel" inputMode="tel" value={form.telefono} disabled={guardando} onChange={(e) => setForm({ ...form, telefono: limpiarTelefono(e.target.value) })} style={input} placeholder="Teléfono / WhatsApp" />
          <input type="email" inputMode="email" value={form.email} disabled={guardando} maxLength={120} onChange={(e) => setForm({ ...form, email: e.target.value.trim() })} style={input} placeholder="Correo (opcional)" />
        </div>
        <input value={form.notas} disabled={guardando} maxLength={300} onChange={(e) => setForm({ ...form, notas: limpiarTexto(e.target.value, 300) })} style={{ ...input, marginBottom: '10px' }} placeholder="Notas: gustos, tallas, preferencias..." />
        {esViajes && (
          <div style={{ background: '#FBFAFF', border: '1px solid #EDE9FE', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
            <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 700, color: '#7C3AED' }}>✈️ Datos de viaje</p>
            <div style={{ marginBottom: '10px' }}>
              <label style={etiquetaFiltro}>Destinos que ha comprado</label>
              <ChipsInput valores={form.destinos} disabled={guardando} sugerencias={destinos.map((o) => o.l)}
                color="#1D4ED8" fondo="#EFF6FF"
                placeholder="Toca para elegir o escribe un destino (ej. Cancún)" onChange={(v) => setForm({ ...form, destinos: v })} />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={etiquetaFiltro}>Servicios que ha comprado</label>
              <ChipsInput valores={form.servicios} disabled={guardando} sugerencias={servicios.map((o) => o.l)}
                placeholder="Toca para elegir o escribe un servicio (ej. Todo incluido)" onChange={(v) => setForm({ ...form, servicios: v })} />
            </div>
            <div>
              <label style={etiquetaFiltro}>Fecha de la última compra</label>
              <input type="date" value={form.fechaUltimaCompra} disabled={guardando}
                onChange={(e) => setForm({ ...form, fechaUltimaCompra: e.target.value })}
                style={{ ...input, maxWidth: '220px' }} />
            </div>
          </div>
        )}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600, display: 'block', marginBottom: '4px' }}>¿De dónde es?</label>
          <UbicacionCliente esLocal={form.esLocal} ciudad={form.ciudad} disabled={guardando}
            onEsLocal={(v) => setForm({ ...form, esLocal: v })} onCiudad={(v) => setForm({ ...form, ciudad: v })} />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="submit" disabled={guardando}
            style={{ padding: '10px 24px', backgroundColor: guardando ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: guardando ? 'wait' : 'pointer' }}>
            {guardando ? '⏳ Guardando...' : editandoId ? 'Guardar cambios' : 'Agregar cliente'}
          </button>
          {editandoId && (
            <button type="button" onClick={() => { setEditandoId(null); setForm(VACIO); }}
              style={{ padding: '10px 20px', backgroundColor: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
              Cancelar
            </button>
          )}
        </div>
      </form>

      {/* Búsqueda + filtros */}
      {clientes && clientes.length > 0 && (
        <div style={{ background: 'white', border: '1px solid #F3F4F6', borderRadius: '12px', padding: '12px', marginBottom: '12px' }}>
          <input type="search" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder={esViajes ? '🔍 Buscar por nombre, teléfono, correo, destino o servicio' : '🔍 Buscar por nombre, teléfono o correo'}
            style={{ ...input, marginBottom: '10px' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', alignItems: 'end' }}>
            <div>
              <label style={etiquetaFiltro}>🎯 Segmento</label>
              <select value={filtroSegmento} onChange={(e) => setFiltroSegmento(e.target.value)} style={selectF}>
                {SEGMENTOS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
            {ciudades.length > 0 && (
              <div>
                <label style={etiquetaFiltro}>📍 Ciudad</label>
                <select value={filtroCiudad} onChange={(e) => setFiltroCiudad(e.target.value)} style={selectF}>
                  <option value="">Todas</option>
                  {ciudades.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}
                </select>
              </div>
            )}
            {productos.length > 0 && (
              <div>
                <label style={etiquetaFiltro}>🛍️ Producto que compró</label>
                <select value={filtroProducto} onChange={(e) => setFiltroProducto(e.target.value)} style={selectF}>
                  <option value="">Todos</option>
                  {productos.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}
                </select>
              </div>
            )}
            {esViajes && destinos.length > 0 && (
              <div>
                <label style={etiquetaFiltro}>✈️ Destino</label>
                <select value={filtroDestino} onChange={(e) => setFiltroDestino(e.target.value)} style={selectF}>
                  <option value="">Todos</option>
                  {destinos.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}
                </select>
              </div>
            )}
            {esViajes && servicios.length > 0 && (
              <div>
                <label style={etiquetaFiltro}>🧳 Servicio</label>
                <select value={filtroServicio} onChange={(e) => setFiltroServicio(e.target.value)} style={selectF}>
                  <option value="">Todos</option>
                  {servicios.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}
                </select>
              </div>
            )}
            <div>
              <label style={etiquetaFiltro}>📅 Compró desde</label>
              <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} style={selectF} />
            </div>
            <div>
              <label style={etiquetaFiltro}>📅 Compró hasta</label>
              <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} style={selectF} />
            </div>
          </div>
          {hayFiltros && (
            <button type="button" onClick={limpiarFiltros}
              style={{ marginTop: '10px', padding: '6px 12px', background: 'none', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '12.5px', cursor: 'pointer', color: '#6B7280' }}>
              ✕ Limpiar filtros
            </button>
          )}
        </div>
      )}

      {/* Barra de selección y acciones en lote */}
      {clientes && clientes.length > 0 && filtrados.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '12px', padding: '10px 12px', borderRadius: '10px', background: sel.size ? '#F5F3FF' : 'transparent', border: sel.size ? '1px solid #DDD6FE' : '1px solid transparent' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={todosFiltradosSel} onChange={toggleTodos} style={{ width: '18px', height: '18px', accentColor: '#7C3AED' }} />
            {todosFiltradosSel ? 'Quitar todos' : `Seleccionar todos (${filtrados.length})`}
          </label>
          <span style={{ fontSize: '13px', color: '#6B7280' }}>
            · {filtrados.length} {filtrados.length === 1 ? 'cliente' : 'clientes'}{hayFiltros || texto ? ' con este filtro' : ''}
          </span>
          {sel.size > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: 'auto', alignItems: 'center' }}>
              <strong style={{ fontSize: '13px', color: '#5B21B6' }}>{sel.size} seleccionado(s)</strong>
              <button type="button" onClick={() => abrirModal('whatsapp')}
                style={{ padding: '7px 12px', background: '#25D366', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>📲 WhatsApp</button>
              <button type="button" onClick={() => abrirModal('correo')}
                style={{ padding: '7px 12px', background: '#2563EB', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>✉️ Correo</button>
              <button type="button" onClick={() => setSel(new Set())}
                style={{ padding: '7px 10px', background: 'white', color: '#6B7280', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>✕</button>
            </div>
          )}
        </div>
      )}

      {!clientes && !error && <div style={{ textAlign: 'center', padding: '32px', color: '#6B7280' }}>⏳ Cargando clientes...</div>}
      {error && <p style={{ textAlign: 'center', padding: '24px', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>}
      {clientes && clientes.length === 0 && (
        <p style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF' }}>Aún no tienes clientes registrados. ¡Agrega el primero! 💜</p>
      )}
      {clientes && clientes.length > 0 && filtrados.length === 0 && (
        <p style={{ textAlign: 'center', padding: '24px', color: '#9CA3AF' }}>Ningún cliente coincide con el filtro.</p>
      )}

      {filtrados.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
          {filtrados.map((c) => {
            const compra = compraPorCliente[c.id];
            const est = estadosPorCliente[c.id];
            const ult = ultimaCompra(c);
            const marcado = sel.has(c.id);
            return (
              <div key={c.id} className="k-card" style={{ padding: '14px', outline: marcado ? '2px solid #7C3AED' : 'none', outlineOffset: '-2px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <input type="checkbox" checked={marcado} onChange={() => toggleSel(c.id)}
                    style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: '#7C3AED', cursor: 'pointer', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 2px', fontWeight: 'bold', color: '#111827' }}>{c.nombre}</p>
                    <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: 600, color: c.es_local === false ? '#0EA5E9' : 'var(--color-primario,#DB2777)' }}>
                      {c.es_local === false ? `🚚 ${c.ciudad || 'Otra ciudad'}` : '📍 De mi ciudad'}
                    </p>
                    {c.telefono && <p style={{ margin: '0 0 2px', fontSize: '13px', color: '#6B7280' }}>📱 {c.telefono}</p>}
                    {c.email && <p style={{ margin: '0 0 2px', fontSize: '12px', color: '#9CA3AF', overflowWrap: 'anywhere' }}>{c.email}</p>}
                    {c.notas && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic' }}>{c.notas}</p>}
                  </div>
                </div>

                {esViajes && (c.destinos || []).length > 0 && (
                  <p style={{ margin: '8px 0 0', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {c.destinos.map((d) => <span key={d} style={{ fontSize: '11.5px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: '999px', padding: '2px 8px', fontWeight: 600 }}>✈️ {d}</span>)}
                  </p>
                )}
                {esViajes && (c.servicios || []).length > 0 && (
                  <p style={{ margin: '6px 0 0', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {c.servicios.map((s) => <span key={s} style={{ fontSize: '11.5px', background: '#F5F3FF', color: '#7C3AED', borderRadius: '999px', padding: '2px 8px', fontWeight: 600 }}>🧳 {s}</span>)}
                  </p>
                )}

                {/* Resumen de seguimiento */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                  {compra && <span style={{ fontSize: '11px', background: '#ECFDF5', color: '#047857', borderRadius: '6px', padding: '2px 7px', fontWeight: 600 }}>🛍️ {compra.num} compra(s) · {fmt(compra.total)}</span>}
                  {!compra && <span style={{ fontSize: '11px', background: '#F3F4F6', color: '#6B7280', borderRadius: '6px', padding: '2px 7px', fontWeight: 600 }}>💤 Sin compras</span>}
                  {est && est.has('convertida') && <span style={{ fontSize: '11px', background: '#FEF3C7', color: '#92400E', borderRadius: '6px', padding: '2px 7px', fontWeight: 600 }}>💰 Cotización convertida</span>}
                  {est && est.has('aprobada') && !est.has('convertida') && <span style={{ fontSize: '11px', background: '#DCFCE7', color: '#166534', borderRadius: '6px', padding: '2px 7px', fontWeight: 600 }}>✅ Cotización aprobada</span>}
                  {est && (est.has('enviada') || est.has('vista')) && !est.has('aprobada') && !est.has('convertida') && <span style={{ fontSize: '11px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: '6px', padding: '2px 7px', fontWeight: 600 }}>👀 Cotizó, sin aprobar</span>}
                </div>
                {ult && <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#059669', fontWeight: 600 }}>📅 Última compra: {fmtFecha(ult)}</p>}

                <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                  {c.telefono && (
                    <a href={linkWhatsAppA(c.telefono, `¡Hola ${(c.nombre || '').split(' ')[0]}! 👋 `)} target="_blank" rel="noopener noreferrer"
                      style={{ padding: '5px 10px', backgroundColor: '#DCFCE7', color: '#15803D', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', textDecoration: 'none' }}>📲 WhatsApp</a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`}
                      style={{ padding: '5px 10px', backgroundColor: '#DBEAFE', color: '#1D4ED8', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', textDecoration: 'none' }}>✉️ Correo</a>
                  )}
                  <button onClick={() => verHistorial(c)} style={{ padding: '5px 10px', backgroundColor: '#F3E8FF', color: '#7C3AED', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>🛍️ Compras</button>
                  <button onClick={() => editar(c)} style={{ padding: '5px 10px', background: 'none', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>✏️</button>
                  {!esVendedora && <button onClick={() => borrar(c)} style={{ padding: '5px 10px', background: 'none', border: '1px solid #FCA5A5', borderRadius: '6px', fontSize: '12px', color: '#DC2626', cursor: 'pointer' }}>🗑️</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal WhatsApp en lote */}
      {modal === 'whatsapp' && (
        <Modal titulo={`📲 WhatsApp a ${sel.size} cliente(s)`} onCerrar={() => setModal(null)}>
          <label style={etiquetaFiltro}>Tu mensaje</label>
          <textarea value={mensaje} onChange={(e) => setMensaje(e.target.value)} rows={4}
            style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }} />
          <p style={{ margin: '4px 0 12px', fontSize: '12px', color: '#9CA3AF' }}>Escribe <b>{'{nombre}'}</b> y se cambia por el nombre de cada cliente.</p>

          {conTelefono.length < seleccionados.length && (
            <p style={{ margin: '0 0 10px', fontSize: '12.5px', color: '#B45309', background: '#FFFBEB', padding: '8px 10px', borderRadius: '8px' }}>
              ⚠️ {seleccionados.length - conTelefono.length} cliente(s) no tienen teléfono y se omiten.
            </p>
          )}

          <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 700, color: '#111827' }}>Opción 1 · Enviar uno por uno</p>
          <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#6B7280' }}>Toca cada cliente: se abre su chat con el mensaje listo, solo le das enviar.</p>
          <div style={{ border: '1px solid #F3F4F6', borderRadius: '10px', maxHeight: '220px', overflowY: 'auto', marginBottom: '14px' }}>
            {conTelefono.map((c) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '8px 10px', borderBottom: '1px solid #F9FAFB' }}>
                <span style={{ fontSize: '13px', color: '#374151', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {abiertos.has(c.id) ? '✅ ' : ''}{c.nombre}
                </span>
                <button type="button" onClick={() => abrirWhatsApp(c)}
                  style={{ padding: '5px 10px', background: abiertos.has(c.id) ? '#E5E7EB' : '#25D366', color: abiertos.has(c.id) ? '#6B7280' : 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', flexShrink: 0 }}>
                  {abiertos.has(c.id) ? 'Abrir de nuevo' : 'Abrir chat'}
                </button>
              </div>
            ))}
          </div>

          <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 700, color: '#111827' }}>Opción 2 · Lista de difusión</p>
          <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#6B7280' }}>Copia los números y crea una "Lista de difusión" en tu WhatsApp para enviar a todos de un solo.</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" onClick={copiarNumeros} disabled={!conTelefono.length}
              style={{ padding: '9px 14px', background: '#EDE9FE', color: '#5B21B6', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>📋 Copiar {conTelefono.length} número(s)</button>
            <button type="button" onClick={copiarMensaje}
              style={{ padding: '9px 14px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>📋 Copiar mensaje</button>
          </div>
        </Modal>
      )}

      {/* Modal correo en lote */}
      {modal === 'correo' && (
        <Modal titulo={`✉️ Correo a ${sel.size} cliente(s)`} onCerrar={() => setModal(null)}>
          {conEmail.length < seleccionados.length && (
            <p style={{ margin: '0 0 10px', fontSize: '12.5px', color: '#B45309', background: '#FFFBEB', padding: '8px 10px', borderRadius: '8px' }}>
              ⚠️ {seleccionados.length - conEmail.length} cliente(s) no tienen correo y se omiten.
            </p>
          )}
          <label style={etiquetaFiltro}>Asunto</label>
          <input value={asunto} onChange={(e) => setAsunto(e.target.value)} maxLength={120} style={{ ...input, marginBottom: '10px' }} placeholder="Ej. Tenemos una oferta para ti" />
          <label style={etiquetaFiltro}>Mensaje</label>
          <textarea value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} rows={6}
            style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Escribe tu mensaje..." />
          <p style={{ margin: '6px 0 12px', fontSize: '12px', color: '#9CA3AF' }}>
            Se abre tu app de correo con los {conEmail.length} destinatario(s) en copia oculta (CCO), para que nadie vea los correos de los demás.
          </p>
          <button type="button" onClick={abrirCorreoLote} disabled={!conEmail.length}
            style={{ width: '100%', padding: '12px', background: conEmail.length ? '#2563EB' : '#9CA3AF', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '14px', cursor: conEmail.length ? 'pointer' : 'not-allowed' }}>
            ✉️ Abrir correo ({conEmail.length})
          </button>
        </Modal>
      )}

      {detalle && (
        <Modal titulo={`🛍️ ${detalle.cliente.nombre}`} onCerrar={() => setDetalle(null)}>
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#374151' }}>
            {detalle.compras.length} compra(s) · Total gastado:{' '}
            <strong style={{ color: 'var(--color-primario,#DB2777)' }}>{fmt(detalle.compras.reduce((s, v) => s + Number(v.total || 0), 0))}</strong>
          </p>
          {detalle.compras.length === 0 && <p style={{ color: '#9CA3AF', fontSize: '13px' }}>Aún no ha comprado. Asocia este cliente al registrar una venta.</p>}
          {detalle.compras.map((v) => (
            <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '8px 0', borderBottom: '1px solid #F3F4F6', fontSize: '13px' }}>
              <span>
                {v.nombre_producto} <span style={{ color: '#9CA3AF' }}>({v.codigo_producto})</span>
                {v.vendido_en && <span style={{ display: 'block', fontSize: '11px', color: '#9CA3AF' }}>{fmtFecha(v.vendido_en)}</span>}
              </span>
              <span style={{ fontWeight: 'bold', color: 'var(--color-primario,#DB2777)', whiteSpace: 'nowrap' }}>{v.cantidad}× · {fmt(v.total)}</span>
            </div>
          ))}
        </Modal>
      )}
    </div>
  );
}
